import React, { useState, useEffect } from 'react';
import { Save, Users, Percent, DollarSign } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConsultantCommissions = () => {
  const [consultants, setConsultants] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payoutSettings, setPayoutSettings] = useState([]);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchConsultants();
    fetchCourses();
  }, []);

  const fetchConsultants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consultants/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const handleEditCommission = (consultant) => {
    setSelectedConsultant(consultant);
    
    // Initialize payout settings from consultant data or create new
    const existingSettings = consultant.course_payout_settings || [];
    const settings = courses.map(course => {
      const existing = existingSettings.find(s => s.course_id === course.id);
      return {
        course_id: course.id,
        course_name: course.name,
        lateral_allowed: course.lateral_entry_allowed,
        regular_payout: existing?.regular_payout || 0,
        lateral_payout: existing?.lateral_payout || 0
      };
    });
    
    setPayoutSettings(settings);
    setShowModal(true);
  };

  const handlePayoutChange = (courseId, field, value) => {
    setPayoutSettings(prev => prev.map(setting => {
      if (setting.course_id === courseId) {
        return { ...setting, [field]: parseFloat(value) || 0 };
      }
      return setting;
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Filter out courses with zero payouts
      const validSettings = payoutSettings.filter(
        s => s.regular_payout > 0 || s.lateral_payout > 0
      );

      await axios.put(
        `${API_URL}/api/consultants/${selectedConsultant.id}`,
        {
          ...selectedConsultant,
          course_payout_settings: validSettings
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Commission settings saved successfully!');
      setShowModal(false);
      fetchConsultants();
    } catch (error) {
      console.error('Error saving commission:', error);
      alert('Failed to save commission settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Consultant Commission Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Set fixed payout amounts per course for each consultant</p>
      </div>

      {/* Info Box */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-semibold text-green-800 mb-2">💰 How Commission Works</h3>
        <ul className="text-sm text-green-700 space-y-1">
          <li>• Set Course-wise Fixed Amount for each Consultant</li>
          <li>• <strong>Regular Payout:</strong> Commission for Regular Entry students</li>
          <li>• <strong>Lateral Payout:</strong> Commission for Lateral Entry students (can be different)</li>
          <li>• Auto-calculated based on Entry Type when student is admitted</li>
        </ul>
      </div>

      {/* Consultants List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Consultants</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consultant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courses with Payout</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {consultants.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    No consultants found
                  </td>
                </tr>
              ) : (
                consultants.map((consultant) => {
                  const payoutCount = (consultant.course_payout_settings || []).filter(
                    s => s.regular_payout > 0 || s.lateral_payout > 0
                  ).length;
                  
                  return (
                    <tr key={consultant.id}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{consultant.name}</div>
                        <div className="text-sm text-gray-500">{consultant.email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {consultant.phone}
                      </td>
                      <td className="px-6 py-4">
                        {payoutCount > 0 ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                            {payoutCount} courses configured
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            Not configured
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleEditCommission(consultant)}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                        >
                          Set Commission
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedConsultant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Commission Settings: {selectedConsultant.name}
            </h2>
            
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Fixed Amount:</strong> Set specific Payout Amount for each Course
              </p>
            </div>

            <div className="space-y-4">
              {payoutSettings.map((setting) => (
                <div key={setting.course_id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-medium text-gray-900">{setting.course_name}</span>
                      {setting.lateral_allowed && (
                        <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                          Lateral Entry
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Regular Entry Payout (₹)
                      </label>
                      <input
                        type="number"
                        min="0"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        value={setting.regular_payout}
                        onChange={(e) => handlePayoutChange(setting.course_id, 'regular_payout', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    
                    {setting.lateral_allowed ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Lateral Entry Payout (₹)
                        </label>
                        <input
                          type="number"
                          min="0"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          value={setting.lateral_payout}
                          onChange={(e) => handlePayoutChange(setting.course_id, 'lateral_payout', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Lateral Entry Payout
                        </label>
                        <input
                          type="text"
                          disabled
                          className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-400"
                          value="N/A"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 justify-end pt-6 border-t mt-6">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400"
              >
                <Save className="h-4 w-4" />
                {loading ? 'Saving...' : 'Save Commission'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantCommissions;
